import { ListChecks } from 'lucide-react'
import type { ReactNode } from 'react'
import { SectionHeader } from '../SectionHeader'
import type { CapabilitiesData } from '@/data/solutions/types'

type CapabilityCardProps = {
  id: string
  title: string
  subtitle: string
  cardColor: string
  accentColor: string
  children: ReactNode
}

type CapabilityItemProps = {
  number: string
  text: string
}

type ContentCapabilitiesProps = CapabilitiesData

const CapabilityCard = ({
  id,
  title,
  subtitle,
  cardColor,
  accentColor,
  children
}: CapabilityCardProps) => {
  return (
    <div className={`rounded-4xl ${cardColor} overflow-hidden`}>
      <div className="flex justify-between px-6 pt-6 pb-4">
        <h3 className="text-xl md:text-2xl font-semibold">{title}</h3>
        <span className="text-lg font-semibold">{id}</span>
      </div>

      <div
        className={`${accentColor} text-white px-6 py-3 font-semibold text-sm md:text-base`}
      >
        {subtitle}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 md:grid-rows-[repeat(6,auto)] md:grid-flow-col gap-y-3 gap-x-6 px-6 py-6 text-sm md:text-base">
        {children}
      </div>
    </div>
  )
}

const CapabilityItem = ({ number, text }: CapabilityItemProps) => {
  return (
    <div className="flex gap-3 items-start leading-snug">
      <span className="font-semibold w-8 shrink-0">{number}</span>
      <span className="flex-1">{text}</span>
    </div>
  )
}

export const ContentCapabilities = ({
  heading,
  cards
}: ContentCapabilitiesProps) => {
  return (
    <div className="space-y-10 md:space-y-14">
      <SectionHeader
        label="Content Capabilities"
        icon={<ListChecks className="h-4 w-4" />}
        heading={heading}
        color="inc-primary-10"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        {cards.map(card => (
          <CapabilityCard
            key={card.id}
            id={card.id}
            title={card.title}
            subtitle={card.subtitle}
            cardColor={card.cardColor}
            accentColor={card.accentColor}
          >
            {card.items.map(item => (
              <CapabilityItem
                key={item.number}
                number={item.number}
                text={item.text}
              />
            ))}
          </CapabilityCard>
        ))}
      </div>
    </div>
  )
}
